import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import {
  ArrowLeft, Settings, Users, Shield, BookOpen,
  RefreshCw, CheckCircle2, WifiOff, Clock, AlertCircle,
  Search, UserCheck, Layers, Activity, ChevronRight, X,
  UserPlus, Loader2, ShieldCheck, Trash2, Pencil,
} from 'lucide-react'
import clsx from 'clsx'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { adApi } from '../../services/adApi'
import { useAdConfigs, useTestAdConnection, useDeleteAdRule } from '../../hooks/useAdDomains'
import { formatDate } from '../../utils/format'
import CreateRuleModal from './CreateRuleModal'

const STATUS_ICONS = {
  Online:  { key: 'online',   cls: 'text-green-600 bg-green-50',  Icon: CheckCircle2 },
  Offline: { key: 'offline',  cls: 'text-gray-500 bg-gray-100',   Icon: WifiOff      },
  Pending: { key: 'pending',  cls: 'text-yellow-600 bg-yellow-50',Icon: Clock        },
  Error:   { key: 'error',    cls: 'text-red-600 bg-red-50',      Icon: AlertCircle  },
}

const ROLE_BADGE = {
  Owner:    'bg-blue-100 text-blue-700',
  Admin:    'bg-purple-100 text-purple-700',
  Operator: 'bg-green-100 text-green-700',
  Viewer:   'bg-gray-100 text-gray-600',
}

function KpiCard({ icon: Icon, label, value, sublabel, colorClass = 'text-blue-600 bg-blue-50' }) {
  return (
    <div className="card p-5 flex items-center gap-4">
      <div className={clsx('rounded-xl p-3 flex-shrink-0', colorClass)}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <div className="text-2xl font-bold text-gray-900">{value ?? '—'}</div>
        <div className="text-sm font-medium text-gray-700">{label}</div>
        {sublabel && <div className="text-xs text-gray-400 mt-0.5">{sublabel}</div>}
      </div>
    </div>
  )
}

function LastSyncKpi({ lastSync, statsLoading }) {
  const { t, i18n } = useTranslation()
  const [, setPulse] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setPulse((n) => n + 1), 60_000)
    return () => clearInterval(id)
  }, [])

  if (statsLoading) {
    return (
      <KpiCard icon={Activity} label={t('domains.dashboard.kpiLastSync')} value="..." colorClass="text-orange-600 bg-orange-50" />
    )
  }

  if (!lastSync) {
    return (
      <KpiCard icon={Activity} label={t('domains.dashboard.kpiLastSync')} value="—" sublabel={t('domains.dashboard.kpiLastSyncNever')} colorClass="text-slate-500 bg-slate-400/15" />
    )
  }

  const at = new Date(lastSync)
  const ageMs = Date.now() - at.getTime()
  const stale = ageMs > 5 * 60 * 1000
  const timeStr = at.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
  const dateStr = formatDate(at)

  return (
    <div className="min-w-0" title={t('domains.dashboard.kpiLastSyncTooltip')}>
      <KpiCard
        icon={Activity}
        label={t('domains.dashboard.kpiLastSync')}
        value={timeStr}
        sublabel={dateStr}
        colorClass={stale ? 'text-slate-500 bg-slate-400/20 border border-slate-300/40' : 'text-orange-600 bg-orange-50'}
      />
    </div>
  )
}

function ContextBadge({ label, onClear }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200">
      {label}
      {onClear && (
        <button onClick={onClear} className="hover:bg-blue-200 rounded-full p-0.5 transition-colors">
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  )
}

function RemoveManagedGroupModal({ groupLabel, groupDn, onConfirm, onClose, loading }) {
  const { t } = useTranslation()
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="card w-full max-w-lg p-6 space-y-4 shadow-2xl">
        <div className="flex items-center gap-3">
          <div className="rounded-xl p-2.5 bg-red-50 text-red-600">
            <Trash2 className="w-5 h-5" />
          </div>
          <h3 className="font-semibold text-gray-900">{t('domains.dashboard.removeTitle')}</h3>
        </div>
        <p className="text-sm text-gray-700">
          <Trans
            i18nKey="domains.dashboard.removeIntro"
            values={{ label: groupLabel }}
            components={{ b: <span className="font-semibold text-gray-900" />, warn: <span className="font-medium text-red-700" /> }}
          />
        </p>
        <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1.5">
          <li><Trans i18nKey="domains.dashboard.removeBullet1" components={{ b: <strong /> }} /></li>
          <li><Trans i18nKey="domains.dashboard.removeBullet2" components={{ b: <strong /> }} /></li>
          <li>{t('domains.dashboard.removeBullet3')}</li>
        </ul>
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
          <p className="font-medium flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <Trans i18nKey="domains.dashboard.removeAdWarningTitle" components={{ b: <strong /> }} />
          </p>
          <p className="mt-1 text-amber-800/90 pl-6">{t('domains.dashboard.removeAdWarning')}</p>
        </div>
        <p className="text-xs text-gray-400 font-mono break-all">{groupDn}</p>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn btn-secondary flex-1 text-sm" disabled={loading}>
            {t('common.buttons.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="btn flex-1 text-sm flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white border-red-600"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            {t('domains.dashboard.removeSubmit')}
          </button>
        </div>
      </div>
    </div>
  )
}

function AdoptModal({ group, onAdopt, onClose, loading }) {
  const { t } = useTranslation()
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="card w-full max-w-md p-6 space-y-4 shadow-2xl">
        <div className="flex items-center gap-3">
          <div className="rounded-xl p-2.5 bg-amber-50 text-amber-600">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <h3 className="font-semibold text-gray-900">{t('domains.dashboard.adoptTitle')}</h3>
        </div>
        <p className="text-sm text-gray-600">
          <Trans
            i18nKey="domains.dashboard.adoptHint"
            values={{ name: group.displayName }}
            components={{ b: <span className="font-medium text-gray-900" /> }}
          />
        </p>
        <p className="text-xs text-gray-400 font-mono truncate">{group.dn}</p>
        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="btn btn-secondary flex-1 text-sm">{t('common.buttons.cancel')}</button>
          <button
            onClick={() => onAdopt(group)}
            disabled={loading}
            className="btn btn-primary flex-1 text-sm flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            {t('domains.dashboard.adoptSubmit')}
          </button>
        </div>
      </div>
    </div>
  )
}

function AddUserToGroupModal({ configId, group, onClose, onAdded }) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [adding, setAdding] = useState(null)
  const [error, setError] = useState(null)
  const timerRef = useRef(null)

  const handleSearch = (e) => {
    const v = e.target.value
    setQuery(v)
    clearTimeout(timerRef.current)
    if (v.length < 2) { setResults([]); return }
    timerRef.current = setTimeout(async () => {
      setSearching(true)
      try { setResults(await adApi.searchUsers(configId, v)) }
      catch { setResults([]) }
      finally { setSearching(false) }
    }, 350)
  }

  const handleAdd = async (user) => {
    setAdding(user.dn)
    setError(null)
    try {
      await adApi.addMember(configId, group.dn ?? group.distinguishedName, user.dn)
      onAdded()
    } catch (e) {
      setError(e?.response?.data?.error || e.message)
    } finally {
      setAdding(null)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="card w-full max-w-lg max-h-[80vh] flex flex-col p-0 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">{t('domains.dashboard.addUserTitle')}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{group.displayName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-100">
          <p className="text-xs text-gray-500 mb-2">{t('domains.dashboard.addUserHint')}</p>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              className="input w-full pl-8 text-sm"
              placeholder={t('domains.dashboard.addUserSearchPlaceholder')}
              value={query}
              onChange={handleSearch}
              autoFocus
            />
            {searching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 animate-spin" />}
          </div>
        </div>

        {error && (
          <div className="mx-4 mt-3 flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{error}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-2 divide-y divide-gray-50">
          {results.length === 0 && !searching && (
            <div className="p-6 text-center text-sm text-gray-400">
              {query.length >= 2 ? t('domains.dashboard.addUserNoResults') : t('domains.dashboard.addUserMinChars')}
            </div>
          )}
          {results.map((u) => (
            <div key={u.dn} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{u.displayName}</p>
                <p className="text-xs text-gray-400">{u.samAccountName}{u.email ? ` · ${u.email}` : ''}</p>
              </div>
              <button
                onClick={() => handleAdd(u)}
                disabled={!!adding}
                className="btn btn-primary text-xs flex-shrink-0 flex items-center gap-1"
              >
                {adding === u.dn ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
                {t('domains.dashboard.addUserAdd')}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function GroupsColumn({ configId, managedGroups, selectedGroup, onSelectGroup, onClearGroup, onRequestRemoveManaged }) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [searchResults, setSearchResults] = useState(null)
  const [loading, setLoading] = useState(false)
  const timerRef = useRef(null)

  const isSearchActive = query.length >= 2
  const displayItems = isSearchActive ? searchResults : managedGroups

  const managedDns = new Set((managedGroups ?? []).map((g) => g.distinguishedName?.toLowerCase()))

  const handleSearch = (e) => {
    const v = e.target.value
    setQuery(v)
    clearTimeout(timerRef.current)
    if (v.length < 2) { setSearchResults(null); return }
    timerRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const data = await adApi.searchGroups(configId, v)
        setSearchResults(data)
      } catch { setSearchResults([]) }
      finally { setLoading(false) }
    }, 400)
  }

  const clearSearch = () => {
    setQuery('')
    setSearchResults(null)
  }

  const isAdopted = (group) => {
    const dn = (group.dn ?? group.distinguishedName ?? '').toLowerCase()
    return managedDns.has(dn)
  }

  return (
    <div className="card flex flex-col min-h-0 h-full max-h-[min(70vh,calc(100vh-200px))] lg:max-h-full">
      <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Layers className="w-5 h-5 text-gray-500" />
          <h3 className="font-semibold text-gray-900">{t('domains.dashboard.groupsTitle')}</h3>
        </div>
        {selectedGroup && (
          <button onClick={onClearGroup} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
            <X className="w-3 h-3" />
            {t('domains.dashboard.clear')}
          </button>
        )}
      </div>

      <div className="p-4 border-b border-gray-100 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={handleSearch}
            placeholder={t('domains.dashboard.groupsSearchPlaceholder')}
            className="input w-full pl-8 text-sm"
          />
          {loading && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 animate-spin" />}
          {isSearchActive && !loading && (
            <button onClick={clearSearch} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-2 divide-y divide-gray-50">
        {!displayItems && (
          <div className="p-6 text-center text-sm text-gray-400">{t('domains.dashboard.groupsLoading')}</div>
        )}
        {displayItems?.length === 0 && (
          <div className="p-6 text-center text-sm text-gray-400">
            {isSearchActive ? t('domains.dashboard.groupsNoResults') : t('domains.dashboard.groupsNoAdopted')}
          </div>
        )}
        {displayItems?.map((g) => {
          const dn = g.dn ?? g.distinguishedName
          const name = g.displayName
          const adopted = isAdopted(g)
          const isSelected = selectedGroup && (selectedGroup.dn ?? selectedGroup.distinguishedName)?.toLowerCase() === dn?.toLowerCase()
          const managedEntry = adopted
            ? (managedGroups ?? []).find((m) => m.distinguishedName?.toLowerCase() === dn?.toLowerCase())
            : null

          return (
            <div
              key={dn}
              className={clsx(
                'flex items-center justify-between gap-2 p-3 cursor-pointer rounded-lg transition-colors',
                isSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50',
              )}
              onClick={() => onSelectGroup(g, adopted)}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-800 truncate">{name}</span>
                  {adopted && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 flex-shrink-0">
                      {t('domains.dashboard.adoptedBadge')}
                    </span>
                  )}
                  {adopted && managedEntry?.id && (
                    <button
                      type="button"
                      title={t('domains.dashboard.removeTooltip')}
                      onClick={(e) => {
                        e.stopPropagation()
                        onRequestRemoveManaged({
                          id: managedEntry.id,
                          displayName: name ?? managedEntry.displayName,
                          dn,
                        })
                      }}
                      className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 flex-shrink-0"
                    >
                      {t('domains.dashboard.removeBadge')}
                    </button>
                  )}
                </div>
                <div className="text-xs text-gray-400 font-mono truncate">{dn}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
            </div>
          )
        })}
      </div>
    </div>
  )
}

function UsersColumn({ configId, selectedGroup, selectedUser, onSelectUser, onClearUser }) {
  const { t } = useTranslation()
  const groupDn = selectedGroup?.dn ?? selectedGroup?.distinguishedName

  const { data: groupMembers, isLoading: membersLoading, refetch: refetchMembers } = useQuery({
    queryKey: ['ad-group-members', configId, groupDn],
    queryFn: () => adApi.getGroupMembers(configId, groupDn),
    enabled: !!configId && !!groupDn,
  })

  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState(null)
  const [searchLoading, setSearchLoading] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const timerRef = useRef(null)

  useEffect(() => {
    setSearchQuery('')
    setSearchResults(null)
  }, [groupDn])

  const handleSearch = (e) => {
    const v = e.target.value
    setSearchQuery(v)
    clearTimeout(timerRef.current)
    if (v.length < 2) { setSearchResults(null); return }
    timerRef.current = setTimeout(async () => {
      setSearchLoading(true)
      try {
        const data = await adApi.searchUsers(configId, v)
        setSearchResults(data)
      } catch { setSearchResults([]) }
      finally { setSearchLoading(false) }
    }, 400)
  }

  const hasGroupContext = !!selectedGroup
  const displayItems = hasGroupContext
    ? (searchQuery.length >= 2 ? searchResults : groupMembers)
    : (searchQuery.length >= 2 ? searchResults : null)

  const handleUserAdded = () => {
    setShowAddModal(false)
    refetchMembers()
  }

  return (
    <div className="card flex flex-col min-h-0 h-full max-h-[min(70vh,calc(100vh-200px))] lg:max-h-full">
      <div className="p-4 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-gray-500" />
            <h3 className="font-semibold text-gray-900">{t('domains.dashboard.usersTitle')}</h3>
          </div>
          <div className="flex items-center gap-2">
            {selectedUser && (
              <button onClick={onClearUser} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
                <X className="w-3 h-3" />
                {t('domains.dashboard.clear')}
              </button>
            )}
            {hasGroupContext && (
              <button onClick={() => setShowAddModal(true)} className="btn btn-primary text-xs flex items-center gap-1">
                <UserPlus className="w-3 h-3" />
                {t('domains.dashboard.addUser')}
              </button>
            )}
          </div>
        </div>
        {hasGroupContext && (
          <div className="flex items-center gap-1.5 mt-1">
            <ContextBadge label={selectedGroup.displayName} />
          </div>
        )}
      </div>

      <div className="p-4 border-b border-gray-100 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder={hasGroupContext ? t('domains.dashboard.usersSearchMembersPlaceholder') : t('domains.dashboard.usersSearchDomainPlaceholder')}
            className="input w-full pl-8 text-sm"
          />
          {searchLoading && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 animate-spin" />}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-2 divide-y divide-gray-50">
        {membersLoading && hasGroupContext && (
          <div className="p-6 text-center text-sm text-gray-400 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />{t('domains.dashboard.usersLoadingMembers')}
          </div>
        )}
        {!hasGroupContext && !searchResults && (
          <div className="p-6 text-center text-sm text-gray-400">
            {t('domains.dashboard.usersEmptyHint')}
          </div>
        )}
        {displayItems?.length === 0 && !membersLoading && (
          <div className="p-6 text-center text-sm text-gray-400">{t('domains.dashboard.usersNoResults')}</div>
        )}
        {displayItems?.map((u) => {
          const dn = u.dn
          const isSelected = selectedUser?.dn === dn
          return (
            <div
              key={dn}
              className={clsx(
                'p-3 cursor-pointer rounded-lg transition-colors',
                isSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50',
              )}
              onClick={() => onSelectUser(u)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-800">{u.displayName}</div>
                  <div className="text-xs text-gray-500">{u.samAccountName}</div>
                  {u.email && <div className="text-xs text-gray-400 truncate">{u.email}</div>}
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0 mt-1" />
              </div>
            </div>
          )
        })}
      </div>

      {showAddModal && (
        <AddUserToGroupModal
          configId={configId}
          group={selectedGroup}
          onClose={() => setShowAddModal(false)}
          onAdded={handleUserAdded}
        />
      )}
    </div>
  )
}

function RulesColumn({ configId, selectedGroup, selectedUser }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data: allRules, isLoading } = useQuery({
    queryKey: ['ad-rules', configId],
    queryFn: () => adApi.getRules(configId),
    enabled: !!configId,
  })
  const deleteRule = useDeleteAdRule(configId)
  const [showCreate, setShowCreate] = useState(false)
  const [editingRule, setEditingRule] = useState(null)

  const handleDeleteRule = async (rule) => {
    const confirmMsg = t('domains.dashboard.deleteRuleConfirm', 'Apagar esta regra? Regras e destinos VPN derivados também serão removidos.')
    if (!window.confirm(confirmMsg)) return
    try {
      await deleteRule.mutateAsync(rule.id)
    } catch (e) {
      alert((e?.response?.data?.error || e?.message || String(e)))
    }
  }

  const groupDn = selectedGroup?.dn ?? selectedGroup?.distinguishedName
  const userMemberOf = selectedUser?.memberOf ?? []

  let filteredRules = allRules ?? []
  let contextLabel = t('domains.dashboard.rulesContextAll')

  if (selectedUser && selectedGroup) {
    const userGroupDns = new Set(userMemberOf.map((d) => d.toLowerCase()))
    userGroupDns.add((groupDn ?? '').toLowerCase())
    filteredRules = filteredRules.filter((r) => {
      if (r.userId === selectedUser.userId) return true
      return userGroupDns.has(r.adGroupDn?.toLowerCase())
    })
    contextLabel = t('domains.dashboard.rulesContextUser')
  } else if (selectedGroup) {
    filteredRules = filteredRules.filter(
      (r) => r.adGroupDn?.toLowerCase() === groupDn?.toLowerCase()
    )
    contextLabel = t('domains.dashboard.rulesContextGroup')
  }

  const canCreate = !!selectedGroup
  const createLabel = selectedUser
    ? t('domains.dashboard.addRuleForUser', 'Regra para este usuário')
    : t('domains.dashboard.addRuleForGroup', 'Regra para este grupo')

  return (
    <div className="card flex flex-col min-h-0 h-full max-h-[min(70vh,calc(100vh-200px))] lg:max-h-full">
      <div className="p-4 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <BookOpen className="w-5 h-5 text-gray-500" />
            <h3 className="font-semibold text-gray-900">{t('domains.dashboard.rulesTitle')}</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">{contextLabel}</span>
            {canCreate && (
              <button
                onClick={() => setShowCreate(true)}
                className="btn btn-primary text-xs flex items-center gap-1"
                title={createLabel}
              >
                <ShieldCheck className="w-3 h-3" />
                {t('domains.dashboard.addRule', 'Adicionar')}
              </button>
            )}
          </div>
        </div>
        {(selectedGroup || selectedUser) && (
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {selectedGroup && <ContextBadge label={selectedGroup.displayName} />}
            {selectedUser && (
              <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200">
                {selectedUser.displayName}
              </span>
            )}
          </div>
        )}
      </div>
      {showCreate && (
        <CreateRuleModal
          configId={configId}
          contextGroup={selectedGroup}
          contextUser={selectedUser}
          onClose={() => setShowCreate(false)}
          onCreated={() => queryClient.invalidateQueries({ queryKey: ['ad-rules', configId] })}
        />
      )}

      {editingRule && (
        <CreateRuleModal
          configId={configId}
          editRule={editingRule}
          onClose={() => setEditingRule(null)}
          onCreated={() => queryClient.invalidateQueries({ queryKey: ['ad-rules', configId] })}
        />
      )}

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-2">
        {isLoading && (
          <div className="text-sm text-gray-400 text-center py-4 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />{t('common.generic.loading')}
          </div>
        )}
        {!isLoading && filteredRules.length === 0 && (
          <div className="text-sm text-gray-400 text-center py-6">
            {selectedGroup || selectedUser ? t('domains.dashboard.rulesEmptyContext') : t('domains.dashboard.rulesEmpty')}
          </div>
        )}
        <div className="space-y-2">
          {filteredRules.map((r) => (
            <div key={r.id} className="bg-gray-50 rounded-lg">
              <div className="p-3 flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-800 truncate">{r.adGroupDisplayName}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={clsx(
                      'text-xs px-1.5 py-0.5 rounded',
                      r.allowAccess ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    )}>
                      {r.allowAccess ? t('domains.dashboard.rulesAllow') : t('domains.dashboard.rulesBlock')}
                    </span>
                    <span className={clsx('text-xs px-1.5 py-0.5 rounded', ROLE_BADGE[r.mappedRole] || 'bg-gray-100 text-gray-600')}>
                      {r.mappedRole}
                    </span>
                    <span className="text-xs text-gray-400">{t('domains.dashboard.rulesPriority', { priority: r.priority })}</span>
                    {r.userId && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 border border-purple-100">
                        {t('domains.dashboard.rulesIndividual')}{r.userName ? `: ${r.userName}` : ''}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => setEditingRule(r)}
                    title={t('common.buttons.edit', 'Editar')}
                    className="p-1.5 rounded hover:bg-white text-gray-400 hover:text-sky-600"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDeleteRule(r)}
                    disabled={deleteRule.isPending}
                    title={t('common.buttons.delete', 'Apagar')}
                    className="p-1.5 rounded hover:bg-white text-gray-400 hover:text-red-600 disabled:opacity-40"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default function DomainDashboard() {
  const { t } = useTranslation()
  const { configId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const testMutation = useTestAdConnection()

  const { data: configs } = useAdConfigs()
  const domain = configs?.find((c) => c.id === configId)

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['ad-stats', configId],
    queryFn: () => adApi.getStats(configId),
    enabled: !!configId,
    refetchInterval: 60_000,
  })

  const { data: managedGroups, refetch: refetchManaged } = useQuery({
    queryKey: ['managed-groups', configId],
    queryFn: () => adApi.getManagedGroups(configId),
    enabled: !!configId,
  })

  const [selectedGroup, setSelectedGroup] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)

  const [adoptTarget, setAdoptTarget] = useState(null)
  const [adopting, setAdopting] = useState(false)
  const [removeTarget, setRemoveTarget] = useState(null)
  const [removing, setRemoving] = useState(false)

  const handleSelectGroup = (group, isAdopted) => {
    if (!isAdopted) {
      setAdoptTarget(group)
      return
    }
    setSelectedGroup(group)
    setSelectedUser(null)
  }

  const handleClearGroup = () => {
    setSelectedGroup(null)
    setSelectedUser(null)
  }

  const handleSelectUser = (user) => { setSelectedUser(user) }

  const handleClearUser = () => { setSelectedUser(null) }

  const handleConfirmRemoveManaged = async () => {
    if (!removeTarget) return
    setRemoving(true)
    try {
      await adApi.unmanageGroup(configId, removeTarget.id)
      await refetchManaged()
      await queryClient.invalidateQueries({ queryKey: ['ad-rules', configId] })
      const selDn = (selectedGroup?.dn ?? selectedGroup?.distinguishedName)?.toLowerCase()
      if (selDn && selDn === removeTarget.dn?.toLowerCase()) {
        setSelectedGroup(null)
        setSelectedUser(null)
      }
      setRemoveTarget(null)
    } catch (e) {
      alert(t('domains.dashboard.removeError') + (e?.response?.data?.error ?? e.message))
    } finally {
      setRemoving(false)
    }
  }

  const handleAdopt = async (group) => {
    setAdopting(true)
    try {
      await adApi.adoptGroup(configId, group.objectGuid)
      await refetchManaged()
      setAdoptTarget(null)
      const refreshed = await adApi.getManagedGroups(configId)
      const adopted = refreshed?.find(
        (g) => g.distinguishedName?.toLowerCase() === group.dn?.toLowerCase()
      )
      if (adopted) {
        setSelectedGroup(adopted)
        setSelectedUser(null)
      }
    } catch (e) {
      alert(t('domains.dashboard.adoptError') + (e?.response?.data?.error ?? e.message))
    } finally {
      setAdopting(false)
    }
  }

  const handleTest = async () => {
    try { await testMutation.mutateAsync(configId) }
    catch (e) { alert(t('domains.dashboard.testError') + e.message) }
  }

  const status = STATUS_ICONS[domain?.connectionStatus] ?? STATUS_ICONS.Pending
  const StatusIcon = status.Icon

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/domains')}
          className="btn btn-secondary text-sm flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.buttons.back')}
        </button>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-500">{t('domains.title')}</span>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <span className="text-sm font-medium text-gray-800">{domain?.displayName ?? '...'}</span>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{domain?.displayName ?? '...'}</h1>
            <span className={clsx('flex items-center gap-1.5 text-sm font-medium px-2.5 py-1 rounded-full', status.cls)}>
              <StatusIcon className="w-3.5 h-3.5" />
              {t(`common.status.${status.key}`)}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500 font-mono">
            {domain?.server}:{domain?.port}{domain?.useSsl ? ' · SSL' : ''} · {domain?.baseDn}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleTest}
            disabled={testMutation.isPending}
            className="btn btn-secondary text-sm flex items-center gap-1"
          >
            <RefreshCw className={clsx('w-4 h-4', testMutation.isPending && 'animate-spin')} />
            {t('domains.test')}
          </button>
          <button
            onClick={() => navigate(`/domains/${configId}/settings`)}
            className="btn btn-secondary text-sm flex items-center gap-1"
          >
            <Settings className="w-4 h-4" />
            {t('domains.dashboard.settings')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          icon={UserCheck}
          label={t('domains.dashboard.kpiAuthUsers')}
          value={statsLoading ? '...' : stats?.totalUsersAuthenticated}
          colorClass="text-blue-600 bg-blue-50"
        />
        <KpiCard
          icon={Layers}
          label={t('domains.dashboard.kpiManagedGroups')}
          value={statsLoading ? '...' : stats?.managedGroups}
          colorClass="text-purple-600 bg-purple-50"
        />
        <KpiCard
          icon={Shield}
          label={t('domains.dashboard.kpiActiveRules')}
          value={statsLoading ? '...' : stats?.activeRules}
          colorClass="text-green-600 bg-green-50"
        />
        <LastSyncKpi lastSync={stats?.lastSync} statsLoading={statsLoading} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:min-h-[min(70vh,calc(100vh-220px))] lg:max-h-[min(70vh,calc(100vh-220px))] lg:items-stretch">
        <GroupsColumn
          configId={configId}
          managedGroups={managedGroups}
          selectedGroup={selectedGroup}
          onSelectGroup={handleSelectGroup}
          onClearGroup={handleClearGroup}
          onRequestRemoveManaged={setRemoveTarget}
        />
        <UsersColumn
          configId={configId}
          selectedGroup={selectedGroup}
          selectedUser={selectedUser}
          onSelectUser={handleSelectUser}
          onClearUser={handleClearUser}
        />
        <RulesColumn
          configId={configId}
          selectedGroup={selectedGroup}
          selectedUser={selectedUser}
        />
      </div>

      {adoptTarget && (
        <AdoptModal
          group={adoptTarget}
          onAdopt={handleAdopt}
          onClose={() => setAdoptTarget(null)}
          loading={adopting}
        />
      )}

      {removeTarget && (
        <RemoveManagedGroupModal
          groupLabel={removeTarget.displayName}
          groupDn={removeTarget.dn}
          onClose={() => !removing && setRemoveTarget(null)}
          onConfirm={handleConfirmRemoveManaged}
          loading={removing}
        />
      )}
    </div>
  )
}
